import { clearAuthStatusCache, getAuthStatus } from '../services/authService.js';
import type { AuthStatus } from '../types/auth.js';
import { tryAcceptPendingInvitation } from '../services/pendingInvitationService.js';
import { capturePendingInvitationFromUrl } from '../utils/pendingInvitation.js';
import {
  getConversation,
  listConversations,
} from '../services/conversationService.js';
import { INITIAL_CONVERSATION_MESSAGE_LIMIT } from '../services/conversationSessionCache.js';
import { getFamilyPack } from '../services/familyService.js';
import { fetchKidSkillsState } from '../services/kidSkillsService.js';
import { KID_SKILLS_CATALOG_FALLBACK } from '../constants/kidSkillsCatalogFallback.js';
import type { KidSkillsState } from '../types/kidSkill.js';
import type { ConversationDetail } from '../types/conversation.js';
import type { ConversationSummary } from '../types/conversation.js';
import type { FamilyPack } from '../types/family.js';
import { runAppBootstrapOnce } from './appBootstrapCoordinator.js';

export type AppBootstrapPrefetchResult =
  | { kind: 'redirect' }
  | {
      kind: 'ready';
      auth: AuthStatus;
      conversations: ConversationSummary[];
      family: FamilyPack | null;
      kidSkillsState: KidSkillsState | null;
      initialConversationPromise: Promise<ConversationDetail | null>;
    };

async function prefetchAppBootstrap(): Promise<AppBootstrapPrefetchResult> {
  capturePendingInvitationFromUrl();

  const listPromise = listConversations().catch(() => [] as ConversationSummary[]);
  const initialConversationPromise = listPromise.then((conversations) => {
    const first = conversations[0];
    if (!first) {
      return null;
    }
    return getConversation(first.id, {
      messageLimit: INITIAL_CONVERSATION_MESSAGE_LIMIT,
    }).catch(() => null);
  });

  const [auth, conversationsResponse, family] = await Promise.all([
    getAuthStatus(),
    listPromise,
    getFamilyPack().catch(() => null),
  ]);

  if (!auth.authenticated) {
    return { kind: 'redirect' };
  }

  const inviteResult = await tryAcceptPendingInvitation({ existingFamilyPack: family });
  let finalAuth = auth;
  if (inviteResult.authRefreshNeeded) {
    clearAuthStatusCache();
    finalAuth = await getAuthStatus({ force: true });
  }

  const conversations = Array.isArray(conversationsResponse) ? conversationsResponse : [];

  let kidSkillsState: KidSkillsState | null = null;
  if (finalAuth.user?.accountRole === 'child') {
    kidSkillsState = await fetchKidSkillsState().catch(
      (): KidSkillsState => ({
        skills: KID_SKILLS_CATALOG_FALLBACK,
        activeKidSkill: finalAuth.activeKidSkill ?? null,
        chosenOn: new Date().toISOString().slice(0, 10),
      })
    );
  }

  return {
    kind: 'ready',
    auth: finalAuth,
    conversations,
    family,
    kidSkillsState,
    initialConversationPromise,
  };
}

/** Start bootstrap network work as early as possible — before the React app bundle parses. */
export function startAppBootstrapPrefetch(): Promise<AppBootstrapPrefetchResult> {
  return runAppBootstrapOnce(prefetchAppBootstrap);
}
